import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/classnames';
import { Spinner } from '@/components/spinner';

const buttonVariants = cva(
  'relative inline-flex items-center gap-[0.8rem] rounded-[0.8rem] align-middle text-[1.4rem] font-semibold leading-normal tracking-[0] transition-colors duration-200 ease-in-out disabled:cursor-not-allowed [&_svg]:h-[2.4rem] [&_svg]:shrink-0',
  {
    variants: {
      align: {
        center: 'justify-center text-center',
        start: 'justify-start text-left',
        between: 'justify-between text-left',
      },
      variant: {
        button:
          'bg-[#57E8BA] text-[#1D1D1D] hover:bg-[#8EF0D1] active:bg-[#4FD3A9] disabled:bg-[#CBF8EA] disabled:text-[#B2B9C7] dark:bg-[#57E8BA] dark:text-[#1D1D1D] dark:hover:bg-[#8EF0D1] dark:active:bg-[#4FD3A9] dark:disabled:bg-[#29443C] dark:disabled:text-[#8BB8AA]',
        default:
          'bg-[#57E8BA] text-[#1D1D1D] hover:bg-[#8EF0D1] active:bg-[#4FD3A9] disabled:bg-[#CBF8EA] disabled:text-[#B2B9C7] dark:bg-[#57E8BA] dark:text-[#1D1D1D] dark:hover:bg-[#8EF0D1] dark:active:bg-[#4FD3A9] dark:disabled:bg-[#29443C] dark:disabled:text-[#8BB8AA]',

        outline:
          'border border-[#EDEDED] bg-[#FFFFFF] text-[#1D1D1D] hover:bg-[#57E8BA] active:bg-[#4FD3A9] disabled:border-[#F2F3F6] disabled:bg-[#FFFFFF] disabled:text-[#B2B9C7] dark:border-[#3A3A3A] dark:bg-[#1C1C1E] dark:text-[#F5F5F5] dark:hover:bg-[#252525] dark:active:bg-[#303030] dark:disabled:border-[#303030] dark:disabled:bg-[#202020] dark:disabled:text-[#69736F]',

        second:
          'border border-[#EDEDED] bg-[#F2F4F3] text-[#1D1D1D] hover:border-[#D6DDDB] hover:bg-[#D6DDDB] active:border-[#D6DDDB] active:bg-[#D6DDDB] disabled:opacity-50 dark:border-[#3A3A3A] dark:bg-[#2A2A2A] dark:text-[#F5F5F5] dark:hover:border-[#4A4A4A] dark:hover:bg-[#333333] dark:disabled:border-[#303030] dark:disabled:bg-[#202020] dark:disabled:text-[#69736F] dark:disabled:opacity-100',

        ghost:
          'bg-transparent text-current hover:bg-black/[0.04] active:bg-black/[0.08] dark:hover:bg-white/[0.06] dark:active:bg-white/[0.1]',

        destructive:
          'bg-red-500 text-white hover:bg-red-600 active:bg-red-700 disabled:opacity-50 dark:bg-red-500 dark:text-white dark:hover:bg-red-600 dark:active:bg-red-700 dark:disabled:bg-red-950 dark:disabled:text-red-300 dark:disabled:opacity-100',

      },

      size: {
        default: 'h-[3.2rem] px-[1rem] py-[0.7rem]',
        sz36: 'h-[3.6rem] px-[1rem] py-[0.9rem]',
        sz38: 'h-[3.8rem] p-[1rem]',
        sz40: 'h-[4rem] px-[1.4rem] py-[0.9rem] text-[1.6rem]',
        sz44: 'h-[4.4rem] px-[1.6rem] py-[1.1rem] text-[1.6rem]',
        sz48: 'h-[4.8rem] px-[1.6rem] py-[1.3rem] text-[1.6rem]',
        sz54: 'h-[5.4rem] px-[2rem] py-[1.6rem] text-[1.8rem]',
        sz60: 'h-[6rem] px-[2.4rem] py-[1.7rem] text-[2rem]',
        lg: 'h-[4rem] px-[1.4rem] py-[0.9rem] text-[1.6rem]',
        icon: 'h-[3.2rem] w-[3.2rem] p-0',

      },
    },

    defaultVariants: {
      align: 'center',
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  cardVisual?: boolean;
  dateRange?: boolean;
  contentClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      align = 'center',
      contentClassName,
      asChild = false,
      loading = false,
      cardVisual = false,
      dateRange = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    if (asChild) {
      return (
        <Comp
          ref={ref}
          className={cn(buttonVariants({ variant, size, align, className }))}
          disabled={disabled || loading}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, align, className }))}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && (
          <span className="absolute inset-0 grid place-items-center">
            <Spinner width={16} height={16} />
          </span>
        )}
        <span
          className={cn(
            'inline-flex min-w-0 max-w-full items-center gap-[0.8rem]',
            align === 'center' && 'justify-center',
            align === 'start' && 'w-full justify-start',
            align === 'between' && 'w-full justify-between',
            cardVisual && 'w-full rounded-[1.4rem]',
            loading && 'invisible',
            dateRange && 'w-full !h-full items-center !text-[1.3rem]',
            contentClassName,
          )}
        >
          {children}
        </span>
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
